import { notification, Space } from 'antd';
import React, { Fragment, useEffect, useState } from 'react';
import RxTemplatePdfView, { RX_RENDER_PDF_DUMMY } from './common/RxTemplatePdfView';
import {
    getDoctorProfile,
    getItemFromMisc,
    getMe,
    getSelectedClinicId,
    MISC_KEYS,
} from '../../store/idbHandler';
import pushRenderPdfTemplateConfig, { TemplateV2 } from '../../api/pushRenderPdfTemplateConfig';
import deleteRenderPdfTemplateConfig from '../../api/deleteRenderPdfTemplateConfig';
import { DocProfileResponse } from '../../api/fetchDoctorProfile';
import AppLoader from '../../components/common/AppLoader';
import { randomString } from '../../store/utils';
import uploadRenderPdfImages from '../../api/uploadRenderPdfImages';
import { track } from '../../tracking';
import { useSelector, useDispatch } from 'react-redux';
import {
    getGenieSetting,
    getPxElementConfig,
    getSectionNameConfig,
} from '../../store/selectors/session';
import { GlobalStateV1 } from '../../store/reducers';
import { GeniePadElementsSettingItem, GenieSettings } from '../../api/pushGenie';
import {
    RenderPdfPrescription,
    TemplateV2 as PrescriptionTemplateV2,
    TemplateConfig,
} from '../../Templar/RenderPdfPrescription';
import fetchPrescriptionTemplate from '../../api/fetchPrescriptionTemplate';
import CustomTemplatePresentModal from './common/CustomTemplatePresentModal';
import ResetRxTemplateModal from './common/ResetRxTemplateModal';
import BlockHistoryEditTemplate from './common/BlockHistoryEditTemplate';
import ImageCrop from './common/ImageCrop';
import LetterHeadCrop from './common/LetterHeadCrop';
import Advices from './components/Advices';
import Diagnosis from './components/Diagnosis';
import FollowUp from './components/FollowUp';
import ExaminationFindings from './components/ExaminationFindings';
import Vitals from './components/Vitals';
import Symptoms from './components/Symptoms';
import PatientDetails from './components/PatientDetails';
import Layout from './components/Layout';
import MedicalHistory from './components/MedicalHistory';
import Medications from './components/Medications';
import DyForm from './components/Dyform';
import LabResults from './components/LabResults';
import LabInvestigations from './components/LabInvestigations';
import Footer from './components/Footer';
import Header from './components/Header';
import ClinicSelectorAndCopy from './components/ClinicSelectorAndCopy';
import SendTemplateWarning from './SendTemplateWarning';
import NavBar from './components/NavBar';
import { setTemplateFieldIfEmpty } from './common/utils';
import ReferredTo from './components/ReferredTo';
import moment from 'moment';
import Notes from './components/Notes';
import Proceduress from './components/PdfProcedures';
import Injections from './components/Injections';
import OpthalAllTables from './components/OpthalAllTables';
import DentalExaminations from './components/dentalExaminations';
import DentalProcedures from './components/dentalProcedures';
import AdmissionAdvised from './components/AdmissionAdvised';
import GrowthChart from './components/GrowthChartIndicators';
import CareCanvas from './components/CareCanvas';
import { fetchDoc } from '../../store/actions/login';
import { IGNORE_CONFIG_KEYS } from '../../constants';
import { PAD_COMPONENTS } from '../../components/prescriptionPad/rightPanel/workspace/PadComponents';
import { RenderPdfConfig } from '../../Templar/types';

export const NO_HEADER = 'no-header';
export const NO_FOOTER = 'no-footer';

export interface LocalTemplateConfig {
    header_border?: boolean;
    footer_border?: boolean;
    header_auto_adjust?: boolean;
    footer_auto_adjust?: boolean;
}

const EditTemplate: React.FC<{ docid: string }> = ({ docid }) => {
    const [docProfile, setdocProfile] = useState<DocProfileResponse | undefined>();
    const dispatch = useDispatch();
    const medElements = useSelector((state) =>
        getGenieSetting(state as GlobalStateV1, 'medElements'),
    ) as GeniePadElementsSettingItem[];
    const padElementsConfig = useSelector(getPxElementConfig);
    const sectionNameConfig = useSelector(getSectionNameConfig);

    const [activeClinic, setActiveClinic] = useState<string | undefined>();

    const [rxConfig, setRxConfig] = useState<TemplateConfig>();
    const [rxLocalConfig, setRxLocalConfig] = useState<LocalTemplateConfig>();
    const [rxData, setRxData] = useState<RenderPdfPrescription>({} as RenderPdfPrescription);
    const [isHeaderLoading, setIsHeaderLoading] = useState(false);
    const [isFooterLoading, setIsFooterLoading] = useState(false);
    const [isCarboneTemplatePresent, setIsCarboneTemplatePresent] = useState(false);
    const [isResetTemplate, setIsResetTemplate] = useState(false);
    const [isConfigChange, setIsConfigChange] = useState(false);
    const [isAttachmentLoading, setIsAttachmentLoading] = useState(false);
    const [cropAttacment, setCropAttachment] = useState(false);
    const [templateTypeTab, setTemplateTypeTab] = useState<'PRINT' | 'SEND'>('PRINT');
    const [showDigitalRxWarning, setShowDigitalRxWarning] = useState(false);
    const [isCopyToSendTemplate, setIsCopyToSendTemplate] = useState(false);

    useEffect(() => {
        try {
            async function fetch() {
                const [prescriptionTemplate] = await Promise.all([fetchPrescriptionTemplate()]);

                const doctorProfile = await getDoctorProfile(docid);
                if (!doctorProfile) {
                    return;
                }

                const doctorCarboneTemplateData =
                    doctorProfile.profile.professional.templates?.print;
                const doctorTemplateData = doctorProfile.profile.professional.templates_v2;

                if (doctorTemplateData?.length) {
                    setIsCarboneTemplatePresent(false);
                } else if (doctorCarboneTemplateData?.length) {
                    setIsCarboneTemplatePresent(true);
                }

                setdocProfile(doctorProfile);
                setActiveClinic(
                    doctorProfile.profile.professional.default_clinic ||
                        doctorProfile.profile.professional.clinics?.[0]._id,
                );
                const allShownDyFroms = (
                    await getItemFromMisc<GenieSettings>(docid, MISC_KEYS.settings)
                ).padElements.filter(
                    (t) => !IGNORE_CONFIG_KEYS.has(t.id) && !PAD_COMPONENTS[t.id] && t.isShown,
                );
                setRxData({
                    ...prescriptionTemplate,
                    tool: {
                        ...prescriptionTemplate.tool,
                        dyform: allShownDyFroms.map((t) => ({
                            id: t.id,
                            name: t.name,
                            elements: [
                                {
                                    id: `e1` + t.id + randomString(4),
                                    name: 'This is SAMPLE heading',
                                    value: 'This is a SAMPLE description for the heading',
                                },
                            ],
                        })),
                    },
                });
            }
            fetch();
        } catch (error) {
            track('ERROR', {
                on: 'EditTemplate file error',
                what: (error as Error).message,
            });
            alert('Rx template data not found.');
        }
    }, []);

    useEffect(() => {
        try {
            if (docProfile) {
                const doctorTemplateData = docProfile.profile.professional.templates_v2;
                const templateData = doctorTemplateData?.find(
                    (data) => data.clinicId === activeClinic && data.type === templateTypeTab,
                );

                if (templateTypeTab === 'SEND') {
                    if (templateData) {
                        closeShowDigitalRxWarning();
                    } else {
                        openShowDigitalRxWarning();
                    }
                }
                setRxConfig(
                    setTemplateFieldIfEmpty(
                        templateData
                            ? {
                                  ...templateData,
                                  header_img:
                                      templateData.header_img &&
                                      templateData.header_img != NO_HEADER
                                          ? `${templateData.header_img}?cacheoff=${randomString(4)}`
                                          : templateData.header_img,
                                  footer_img:
                                      templateData.footer_img &&
                                      templateData.footer_img != NO_FOOTER
                                          ? `${templateData.footer_img}?cacheoff=${randomString(4)}`
                                          : templateData.footer_img,
                                  attachment_image: templateData.attachment_image
                                      ? `${templateData.attachment_image}?cacheoff=${randomString(
                                            4,
                                        )}`
                                      : templateData.attachment_image,
                              }
                            : templateData,
                    ),
                );
            }
        } catch (e) {
            notification.error({
                message: 'Doctor Profile Not Found!',
                duration: 5,
                placement: 'topRight',
            });
        }
    }, [docProfile, activeClinic, templateTypeTab]);

    const saveRxConfigInState = (rxObj: TemplateConfig) => {
        setIsConfigChange(true);
        setRxConfig((preRxConfig) => ({
            ...preRxConfig,
            ...rxObj,
        }));
    };

    const saveRxLocalConfigInState = (rxObj: LocalTemplateConfig) => {
        setRxLocalConfig({
            ...rxLocalConfig,
            ...rxObj,
        });
    };

    const buildConfigObject = (
        rxConfig?: TemplateConfig,
        type?: 'PRINT' | 'SEND',
    ): RenderPdfConfig | undefined => {
        if (!docProfile?._id || !activeClinic || !rxConfig) {
            return;
        }

        if (type === 'SEND') {
            return {
                docid: docProfile._id,
                clinicId: activeClinic,
                header_img:
                    rxConfig.header_img === NO_HEADER
                        ? undefined
                        : (rxConfig.header_img || '')?.split('?')?.[0],
                footer_img:
                    rxConfig.footer_img === NO_FOOTER
                        ? undefined
                        : (rxConfig.footer_img || '')?.split('?')?.[0],
                show_signature: rxConfig.show_signature,
                show_name_in_signature: rxConfig.show_name_in_signature,
                show_signature_text: rxConfig.show_signature_text,
                show_page_number: rxConfig.show_page_number,
                show_prescription_id: rxConfig.show_prescription_id,
                type: 'SEND',
                page_size: 'A4',
                margin_left: rxConfig.margin_left || '',
                margin_right: rxConfig.margin_right || '',
                header_height: rxConfig.header_height || '',
                footer_height: rxConfig.footer_height || '',
                bullets_config: rxConfig.bullets_config,
                tabular_config: rxConfig.tabular_config,
                prescription_size: rxConfig.prescription_size,
                date_and_time: rxConfig.date_and_time,
                header_top_margin: rxConfig.header_top_margin,
                header_bottom_margin: rxConfig.header_bottom_margin,
                header_left_margin: rxConfig.header_left_margin,
                header_right_margin: rxConfig.header_right_margin,
                footer_top_margin: rxConfig.footer_top_margin,
                footer_bottom_margin: rxConfig.footer_bottom_margin,
                footer_left_margin: rxConfig.footer_left_margin,
                footer_right_margin: rxConfig.footer_right_margin,
                lab_vitals_format: rxConfig.lab_vitals_format,
                show_not_valid_for_medical_legal_purpose_message:
                    rxConfig.show_not_valid_for_medical_legal_purpose_message,
                show_eka_logo: rxConfig.show_eka_logo,
                medication_table_format: rxConfig.medication_table_format,
                advices_format: rxConfig.advices_format,
                medication_name_in_capital: rxConfig?.medication_name_in_capital,
                make_generic_name_as_primary: rxConfig?.make_generic_name_as_primary,
                symptoms_name_in_capital: rxConfig.symptoms_name_in_capital,
                diagnosis_name_in_capital: rxConfig.diagnosis_name_in_capital,
                lab_tests_name_in_capital: rxConfig.lab_tests_name_in_capital,
                symptoms_name_in_unbold: rxConfig.symptoms_name_in_unbold,
                diagnosis_name_in_unbold: rxConfig.diagnosis_name_in_unbold,
                lab_tests_name_in_unbold: rxConfig.lab_tests_name_in_unbold,
                lab_vitals_name_in_unbold: rxConfig.lab_vitals_name_in_unbold,
                medical_history_name_in_unbold: rxConfig.medical_history_name_in_unbold,
                vitals_in_unbold: rxConfig.vitals_in_unbold,
                examination_findings_name_in_unbold: rxConfig.examination_findings_name_in_unbold,
                patient_details_format: rxConfig.patient_details_format,
                referred_to_in_unbold: rxConfig.referred_to_in_unbold,
                dyform_in_unbold: rxConfig.dyform_in_unbold,
                dyform_data_layout: rxConfig.dyform_data_layout,
                dyform_heading_color: rxConfig.dyform_heading_color,
                dyform_name_color: rxConfig.dyform_name_color,
                dyform_properties_color: rxConfig.dyform_properties_color,
                patient_form_data_in_unbold: rxConfig.patient_form_data_in_unbold,
                followup_in_unbold: rxConfig.followup_in_unbold,
                date_in_unbold: rxConfig.date_in_unbold,
                patient_form_data_format: rxConfig.patient_form_data_format,
                attachment_image: (rxConfig.attachment_image || '')?.split('?')?.[0],
                template_column_type: rxConfig.template_column_type,
                columns_config: rxConfig.columns_config || {},
                symptoms_heading_color: rxConfig.symptoms_heading_color,
                symptoms_name_color: rxConfig.symptoms_name_color,
                symptoms_properties_color: rxConfig.symptoms_properties_color,
                diagnosis_heading_color: rxConfig.diagnosis_heading_color,
                diagnosis_name_color: rxConfig.diagnosis_name_color,
                diagnosis_properties_color: rxConfig.diagnosis_properties_color,
                vitals_heading_color: rxConfig.vitals_heading_color,
                vitals_name_color: rxConfig.vitals_name_color,
                vitals_properties_color: rxConfig.vitals_properties_color,
                lab_tests_heading_color: rxConfig.lab_tests_heading_color,
                lab_tests_name_color: rxConfig.lab_tests_name_color,
                lab_tests_properties_color: rxConfig.lab_tests_properties_color,
                lab_vitals_heading_color: rxConfig.lab_vitals_heading_color,
                lab_vitals_name_color: rxConfig.lab_vitals_name_color,
                lab_vitals_properties_color: rxConfig.lab_vitals_properties_color,
                referred_to_heading_color: rxConfig.referred_to_heading_color,
                referred_to_properties_color: rxConfig.referred_to_properties_color,
                advices_heading_color: rxConfig.advices_heading_color,
                advices_properties_color: rxConfig.advices_properties_color,
                medications_table_border_color: rxConfig.medications_table_border_color,
                medications_table_title_bg_color: rxConfig.medications_table_title_bg_color,
                pmh_heading_color: rxConfig.pmh_heading_color,
                pmh_name_color: rxConfig.pmh_name_color,
                pmh_properties_color: rxConfig.pmh_properties_color,
                examinations_heading_color: rxConfig.examinations_heading_color,
                examinations_name_color: rxConfig.examinations_name_color,
                examinations_properties_color: rxConfig.examinations_properties_color,
                notes_heading_color: rxConfig.notes_heading_color,
                notes_properties_color: rxConfig.notes_properties_color,
                followup_heading_color: rxConfig.followup_heading_color,
                followup_properties_color: rxConfig.followup_properties_color,
                spacing_between_sections: rxConfig.spacing_between_sections,
                patient_form_below_border: rxConfig.patient_form_below_border,
                patient_details_in_uppercase: rxConfig.patient_details_in_uppercase,
                floating_footer_details: rxConfig?.floating_footer_details,
                medication_heading_hide: rxConfig?.medication_heading_hide,
                medication_table_heading_text: rxConfig?.medication_table_heading_text,
                patient_details_form_data_name_color:
                    rxConfig?.patient_details_form_data_name_color,
                patient_details_form_data_properties_color:
                    rxConfig?.patient_details_form_data_properties_color,
                patient_details_patient_name_color: rxConfig?.patient_details_patient_name_color,
                patient_details_date_color: rxConfig?.patient_details_date_color,
                medications_table_title_alignment: rxConfig?.medications_table_title_alignment,
                patient_medical_history_heading_color:
                    rxConfig?.patient_medical_history_heading_color,
                patient_medical_history_name_color: rxConfig?.patient_medical_history_name_color,
                patient_medical_history_properties_color:
                    rxConfig?.patient_medical_history_properties_color,
                family_history_heading_color: rxConfig?.family_history_heading_color,
                family_history_name_color: rxConfig?.family_history_name_color,
                family_history_properties_color: rxConfig?.family_history_properties_color,
                lifestyle_habits_heading_color: rxConfig?.lifestyle_habits_heading_color,
                lifestyle_habits_name_color: rxConfig?.lifestyle_habits_name_color,
                lifestyle_habits_properties_color: rxConfig?.lifestyle_habits_properties_color,
                travel_history_heading_color: rxConfig?.travel_history_heading_color,
                travel_history_name_color: rxConfig?.travel_history_name_color,
                travel_history_properties_color: rxConfig?.travel_history_properties_color,
                current_medications_heading_color: rxConfig?.current_medications_heading_color,
                current_medications_name_color: rxConfig?.current_medications_name_color,
                current_medications_properties_color:
                    rxConfig?.current_medications_properties_color,
                drug_allergies_heading_color: rxConfig?.drug_allergies_heading_color,
                drug_allergies_name_color: rxConfig?.drug_allergies_name_color,
                drug_allergies_properties_color: rxConfig?.drug_allergies_properties_color,
                other_allergies_heading_color: rxConfig?.other_allergies_heading_color,
                other_allergies_name_color: rxConfig?.other_allergies_name_color,
                other_allergies_properties_color: rxConfig?.other_allergies_properties_color,
                past_procedure_heading_color: rxConfig?.past_procedure_heading_color,
                past_procedure_name_color: rxConfig?.past_procedure_name_color,
                past_procedure_properties_color: rxConfig?.past_procedure_properties_color,
                other_medical_history_heading_color: rxConfig?.other_medical_history_heading_color,
                other_medical_history_name_color: rxConfig?.other_medical_history_name_color,
                other_medical_history_properties_color:
                    rxConfig?.other_medical_history_properties_color,
                footer_doctor_name_color: rxConfig?.footer_doctor_name_color,
                rx_element_key_seperator: rxConfig?.rx_element_key_seperator,
                medications_table_header_color: rxConfig?.medications_table_header_color,
                followup_date_color: rxConfig?.followup_date_color,
                medication_table_width: rxConfig?.medication_table_width || {},
                show_approval_details: rxConfig?.show_approval_details,
                procedures_in_unbold: rxConfig.procedures_in_unbold,
                procedures_heading_color: rxConfig.procedures_heading_color,
                procedures_name_color: rxConfig.procedures_name_color,
                procedures_properties_color: rxConfig.procedures_properties_color,
                injections_table_format: rxConfig.injections_table_format,
                injections_name_in_capital: rxConfig.injections_name_in_capital,
                injections_heading_hide: rxConfig.injections_heading_hide,
                injections_table_heading_text: rxConfig.injections_table_heading_text,
                injections_table_title_bg_color: rxConfig.injections_table_title_bg_color,
                injections_table_border_color: rxConfig.injections_table_border_color,
                injections_table_header_color: rxConfig.injections_table_header_color,
                injections_table_title_alignment: rxConfig.injections_table_title_alignment,
                current_injections_heading_color: rxConfig.current_injections_heading_color,
                current_injections_name_color: rxConfig.current_injections_name_color,
                current_injections_properties_color: rxConfig.current_injections_properties_color,
                injections_table_width: rxConfig.injections_table_width || {},
                opthal_heading_color: rxConfig.opthal_heading_color,
                dental_examinations_heading_color: rxConfig.dental_examinations_heading_color,
                dental_examinations_properties_color: rxConfig.dental_examinations_properties_color,
                dental_examinations_key_color: rxConfig.dental_examinations_key_color,
                dental_procedures_heading_color: rxConfig.dental_procedures_heading_color,
                dental_procedures_table_heading_color:
                    rxConfig?.dental_procedures_table_heading_color,
                dental_procedures_table_border_color:
                    rxConfig?.dental_procedures_table_border_color,
                opthal_table_heading_color: rxConfig?.opthal_table_heading_color,
                opthal_table_border_color: rxConfig?.opthal_table_border_color,
                admission_advised_heading_color: rxConfig?.admission_advised_heading_color,
                admission_advised_name_color: rxConfig?.admission_advised_name_color,
                admission_advised_properties_color: rxConfig?.admission_advised_properties_color,
                growth_chart_heading_color: rxConfig?.growth_chart_heading_color,
                growth_chart_name_color: rxConfig?.growth_chart_name_color,
                growth_chart_properties_color: rxConfig?.growth_chart_properties_color,
                care_canvas_heading_color: rxConfig?.care_canvas_heading_color,
                medical_history_status_display: rxConfig?.medical_history_status_display,
            };
        }
        return {
            docid: docProfile._id,
            clinicId: activeClinic,
            header_img: (rxConfig.header_img || '')?.split('?')?.[0],
            footer_img: (rxConfig.footer_img || '')?.split('?')?.[0],
            show_signature: rxConfig.show_signature,
            show_name_in_signature: rxConfig.show_name_in_signature,
            show_signature_text: rxConfig.show_signature_text,
            show_page_number: rxConfig.show_page_number,
            show_prescription_id: rxConfig.show_prescription_id,
            type: 'PRINT',
            page_size: 'A4',
            margin_left: rxConfig.margin_left || '',
            margin_right: rxConfig.margin_right || '',
            header_height: rxConfig.header_height || '',
            footer_height: rxConfig.footer_height || '',
            bullets_config: rxConfig.bullets_config,
            tabular_config: rxConfig.tabular_config,
            prescription_size: rxConfig.prescription_size,
            date_and_time: rxConfig.date_and_time,
            header_top_margin: rxConfig.header_top_margin,
            header_bottom_margin: rxConfig.header_bottom_margin,
            header_left_margin: rxConfig.header_left_margin,
            header_right_margin: rxConfig.header_right_margin,
            footer_top_margin: rxConfig.footer_top_margin,
            footer_bottom_margin: rxConfig.footer_bottom_margin,
            footer_left_margin: rxConfig.footer_left_margin,
            footer_right_margin: rxConfig.footer_right_margin,
            floating_header: rxConfig.floating_header,
            floating_patient_details: rxConfig.floating_patient_details,
            floating_patient_details_top_margin: rxConfig.floating_patient_details_top_margin,
            floating_footer: rxConfig.floating_footer,
            lab_vitals_format: rxConfig.lab_vitals_format,
            show_not_valid_for_medical_legal_purpose_message:
                rxConfig.show_not_valid_for_medical_legal_purpose_message,
            show_eka_logo: rxConfig.show_eka_logo,
            medication_table_format: rxConfig.medication_table_format,
            floating_header_page_top_margin: rxConfig.floating_header_page_top_margin,
            floating_footer_page_bottom_margin: rxConfig.floating_footer_page_bottom_margin,
            advices_format: rxConfig.advices_format,
            medication_name_in_capital: rxConfig?.medication_name_in_capital,
            make_generic_name_as_primary: rxConfig?.make_generic_name_as_primary,
            symptoms_name_in_capital: rxConfig.symptoms_name_in_capital,
            diagnosis_name_in_capital: rxConfig.diagnosis_name_in_capital,
            lab_tests_name_in_capital: rxConfig.lab_tests_name_in_capital,
            symptoms_name_in_unbold: rxConfig.symptoms_name_in_unbold,
            diagnosis_name_in_unbold: rxConfig.diagnosis_name_in_unbold,
            lab_tests_name_in_unbold: rxConfig.lab_tests_name_in_unbold,
            lab_vitals_name_in_unbold: rxConfig.lab_vitals_name_in_unbold,
            medical_history_name_in_unbold: rxConfig.medical_history_name_in_unbold,
            vitals_in_unbold: rxConfig.vitals_in_unbold,
            examination_findings_name_in_unbold: rxConfig.examination_findings_name_in_unbold,
            patient_details_format: rxConfig.patient_details_format,
            referred_to_in_unbold: rxConfig.referred_to_in_unbold,
            dyform_in_unbold: rxConfig.dyform_in_unbold,
            dyform_data_layout: rxConfig.dyform_data_layout,
            dyform_heading_color: rxConfig.dyform_heading_color,
            dyform_name_color: rxConfig.dyform_name_color,
            dyform_properties_color: rxConfig.dyform_properties_color,
            patient_form_data_in_unbold: rxConfig.patient_form_data_in_unbold,
            followup_in_unbold: rxConfig.followup_in_unbold,
            date_in_unbold: rxConfig.date_in_unbold,
            patient_form_data_format: rxConfig.patient_form_data_format,
            attachment_image: (rxConfig.attachment_image || '')?.split('?')?.[0],
            template_column_type: rxConfig.template_column_type,
            columns_config: rxConfig.columns_config || {},
            symptoms_heading_color: rxConfig.symptoms_heading_color,
            symptoms_name_color: rxConfig.symptoms_name_color,
            symptoms_properties_color: rxConfig.symptoms_properties_color,
            diagnosis_heading_color: rxConfig.diagnosis_heading_color,
            diagnosis_name_color: rxConfig.diagnosis_name_color,
            diagnosis_properties_color: rxConfig.diagnosis_properties_color,
            vitals_heading_color: rxConfig.vitals_heading_color,
            vitals_name_color: rxConfig.vitals_name_color,
            vitals_properties_color: rxConfig.vitals_properties_color,
            lab_tests_heading_color: rxConfig.lab_tests_heading_color,
            lab_tests_name_color: rxConfig.lab_tests_name_color,
            lab_tests_properties_color: rxConfig.lab_tests_properties_color,
            lab_vitals_heading_color: rxConfig.lab_vitals_heading_color,
            lab_vitals_name_color: rxConfig.lab_vitals_name_color,
            lab_vitals_properties_color: rxConfig.lab_vitals_properties_color,
            referred_to_heading_color: rxConfig.referred_to_heading_color,
            referred_to_properties_color: rxConfig.referred_to_properties_color,
            advices_heading_color: rxConfig.advices_heading_color,
            advices_properties_color: rxConfig.advices_properties_color,
            medications_table_border_color: rxConfig.medications_table_border_color,
            medications_table_title_bg_color: rxConfig.medications_table_title_bg_color,
            pmh_heading_color: rxConfig.pmh_heading_color,
            pmh_name_color: rxConfig.pmh_name_color,
            pmh_properties_color: rxConfig.pmh_properties_color,
            examinations_heading_color: rxConfig.examinations_heading_color,
            examinations_name_color: rxConfig.examinations_name_color,
            examinations_properties_color: rxConfig.examinations_properties_color,
            notes_heading_color: rxConfig.notes_heading_color,
            notes_properties_color: rxConfig.notes_properties_color,
            followup_heading_color: rxConfig.followup_heading_color,
            followup_properties_color: rxConfig.followup_properties_color,
            spacing_between_sections: rxConfig.spacing_between_sections,
            patient_form_below_border: rxConfig.patient_form_below_border,
            patient_details_in_uppercase: rxConfig.patient_details_in_uppercase,
            floating_footer_details: rxConfig?.floating_footer_details,
            medication_heading_hide: rxConfig?.medication_heading_hide,
            medication_table_heading_text: rxConfig?.medication_table_heading_text,
            patient_details_form_data_name_color: rxConfig?.patient_details_form_data_name_color,
            patient_details_form_data_properties_color:
                rxConfig?.patient_details_form_data_properties_color,
            patient_details_patient_name_color: rxConfig?.patient_details_patient_name_color,
            patient_details_date_color: rxConfig?.patient_details_date_color,
            medications_table_title_alignment: rxConfig?.medications_table_title_alignment,
            patient_medical_history_heading_color: rxConfig?.patient_medical_history_heading_color,
            patient_medical_history_name_color: rxConfig?.patient_medical_history_name_color,
            patient_medical_history_properties_color:
                rxConfig?.patient_medical_history_properties_color,
            family_history_heading_color: rxConfig?.family_history_heading_color,
            family_history_name_color: rxConfig?.family_history_name_color,
            family_history_properties_color: rxConfig?.family_history_properties_color,
            lifestyle_habits_heading_color: rxConfig?.lifestyle_habits_heading_color,
            lifestyle_habits_name_color: rxConfig?.lifestyle_habits_name_color,
            lifestyle_habits_properties_color: rxConfig?.lifestyle_habits_properties_color,
            travel_history_heading_color: rxConfig?.travel_history_heading_color,
            travel_history_name_color: rxConfig?.travel_history_name_color,
            travel_history_properties_color: rxConfig?.travel_history_properties_color,
            current_medications_heading_color: rxConfig?.current_medications_heading_color,
            current_medications_name_color: rxConfig?.current_medications_name_color,
            current_medications_properties_color: rxConfig?.current_medications_properties_color,
            drug_allergies_heading_color: rxConfig?.drug_allergies_heading_color,
            drug_allergies_name_color: rxConfig?.drug_allergies_name_color,
            drug_allergies_properties_color: rxConfig?.drug_allergies_properties_color,
            other_allergies_heading_color: rxConfig?.other_allergies_heading_color,
            other_allergies_name_color: rxConfig?.other_allergies_name_color,
            other_allergies_properties_color: rxConfig?.other_allergies_properties_color,
            past_procedure_heading_color: rxConfig?.past_procedure_heading_color,
            past_procedure_name_color: rxConfig?.past_procedure_name_color,
            past_procedure_properties_color: rxConfig?.past_procedure_properties_color,
            other_medical_history_heading_color: rxConfig?.other_medical_history_heading_color,
            other_medical_history_name_color: rxConfig?.other_medical_history_name_color,
            other_medical_history_properties_color:
                rxConfig?.other_medical_history_properties_color,
            footer_doctor_name_color: rxConfig?.footer_doctor_name_color,
            show_approval_details: rxConfig?.show_approval_details,
            rx_element_key_seperator: rxConfig?.rx_element_key_seperator,
            medications_table_header_color: rxConfig?.medications_table_header_color,
            followup_date_color: rxConfig?.followup_date_color,
            medication_table_width: rxConfig?.medication_table_width || {},
            procedures_in_unbold: rxConfig.procedures_in_unbold,
            procedures_heading_color: rxConfig.procedures_heading_color,
            procedures_name_color: rxConfig.procedures_name_color,
            procedures_properties_color: rxConfig.procedures_properties_color,
            injections_table_format: rxConfig.injections_table_format,
            injections_name_in_capital: rxConfig.injections_name_in_capital,
            injections_heading_hide: rxConfig.injections_heading_hide,
            injections_table_heading_text: rxConfig.injections_table_heading_text,
            injections_table_title_bg_color: rxConfig.injections_table_title_bg_color,
            injections_table_border_color: rxConfig.injections_table_border_color,
            injections_table_header_color: rxConfig.injections_table_header_color,
            injections_table_title_alignment: rxConfig.injections_table_title_alignment,
            current_injections_heading_color: rxConfig.current_injections_heading_color,
            current_injections_name_color: rxConfig.current_injections_name_color,
            current_injections_properties_color: rxConfig.current_injections_properties_color,
            injections_table_width: rxConfig.injections_table_width || {},
            opthal_heading_color: rxConfig.opthal_heading_color,
            dental_examinations_heading_color: rxConfig.dental_examinations_heading_color,
            dental_examinations_properties_color: rxConfig.dental_examinations_properties_color,
            dental_examinations_key_color: rxConfig.dental_examinations_key_color,
            dental_procedures_heading_color: rxConfig.dental_procedures_heading_color,
            dental_procedures_table_heading_color: rxConfig?.dental_procedures_table_heading_color,
            dental_procedures_table_border_color: rxConfig?.dental_procedures_table_border_color,
            opthal_table_heading_color: rxConfig?.opthal_table_heading_color,
            opthal_table_border_color: rxConfig?.opthal_table_border_color,
            admission_advised_heading_color: rxConfig?.admission_advised_heading_color,
            admission_advised_name_color: rxConfig?.admission_advised_name_color,
            admission_advised_properties_color: rxConfig?.admission_advised_properties_color,
            growth_chart_heading_color: rxConfig?.growth_chart_heading_color,
            growth_chart_name_color: rxConfig?.growth_chart_name_color,
            growth_chart_properties_color: rxConfig?.growth_chart_properties_color,
            care_canvas_heading_color: rxConfig?.care_canvas_heading_color,
            medical_history_status_display: rxConfig?.medical_history_status_display,
        };
    };

    const saveConfigToDb = async (rxConf: TemplateV2, onComplete?: (status: boolean) => void) => {
        try {
            await pushRenderPdfTemplateConfig(docProfile?._id || '', rxConf);
            await dispatch(
                fetchDoc(docProfile?._id, () => {
                    onComplete?.(true);
                }),
            );
            notification.success({
                message: 'New Template Saved!',
                duration: 5,
                placement: 'topRight',
            });

            track('SAVE_EDIT_TEMPLATE', {
                doctorName: docProfile?._id,
                clinicId: activeClinic,
            });

            onComplete?.(true);
        } catch (e) {
            notification.error({
                message: (e as Error).message,
                duration: 5,
                placement: 'topRight',
            });
            track('ERROR', { on: 'Push render pdf template config', what: (e as Error).message });
            onComplete?.(false);
        }
        setIsConfigChange(false);
    };

    const deleteConfigFromDb = () => {
        if (!docProfile?._id || !activeClinic || !templateTypeTab) {
            return;
        }
        deleteRenderPdfTemplateConfig({
            docid: docProfile?._id,
            clinicId: activeClinic,
            type: templateTypeTab,
        });
        setRxConfig(setTemplateFieldIfEmpty(undefined));

        track('RESET_EDIT_TEMPLATE', {
            doctorName: docProfile?._id,
            clinicId: activeClinic,
        });
    };

    const onResetTemplate = () => {
        setIsResetTemplate(true);
    };

    const closeIsResetTemplate = () => {
        setIsResetTemplate(false);
    };

    const onDeleteTemplate = () => {
        deleteConfigFromDb();
        closeIsResetTemplate();
    };

    const closeIsCopyToSendTemplate = () => {
        setIsCopyToSendTemplate(false);
    };

    const openIsCopyToSendTemplate = () => {
        setIsCopyToSendTemplate(true);
    };

    const openShowDigitalRxWarning = () => {
        setShowDigitalRxWarning(true);
    };

    const closeShowDigitalRxWarning = () => {
        setShowDigitalRxWarning(false);
    };

    const onSave = () => {
        const rxConf = buildConfigObject(rxConfig, templateTypeTab);

        if (!rxConf) {
            return;
        }

        saveConfigToDb(rxConf);
    };

    const acknowledgeOldTemplate = () => {
        setIsCarboneTemplatePresent(false);
    };

    const onClinicChange = (id: string) => {
        getDoctorProfile(docid)
            .then((res) => {
                setdocProfile(res);
                setActiveClinic(id);
            })
            .catch((e) => {
                alert('Associated doctor profile not found. Please contact contact@eka.care');
            });
    };

    const onTemplateTypeChange = (type: 'PRINT' | 'SEND') => {
        getDoctorProfile(docid)
            .then((res) => {
                setdocProfile(res);
                setTemplateTypeTab(type);
            })
            .catch((e) => {
                alert('Associated doctor profile not found. Please contact contact@eka.care');
            });
    };

    const uploadHeaderImage = async (file: File, type: 'PRINT' | 'SEND') => {
        setIsHeaderLoading(true);
        if (!docProfile?._id && !activeClinic) {
            notification.error({
                message: 'Upload failed. Doctor or Clinic id not found',
                duration: 5,
                placement: 'topRight',
            });
            return;
        }

        let newFile = new File(
            [file],
            `${docProfile?._id}-${activeClinic}-header.${file.type.split('/')[1]}`,
        );

        if (type === 'SEND') {
            newFile = new File(
                [file],
                `${docProfile?._id}-${activeClinic}-header-send.${file.type.split('/')[1]}`,
            );
        }

        const formData = new FormData();
        formData.append('header-footer', newFile);

        try {
            const data = await uploadRenderPdfImages(formData);

            if (data?.success) {
                saveRxConfigInState({
                    header_img: `${data.url}?cacheoff=${randomString(4)}`,
                });
            } else {
                notification.error({
                    message: 'Failed to save Header Image',
                    duration: 5,
                });
                saveRxConfigInState({
                    header_img: '',
                });
            }
        } catch (error) {
            notification.error({
                message: 'Failed to save Header Image',
                duration: 5,
            });
            console.error(error);
        }
        setIsHeaderLoading(false);
    };

    const uploadFooterImage = async (file: File, type: 'PRINT' | 'SEND') => {
        setIsFooterLoading(true);
        if (!docProfile?._id && !activeClinic) {
            notification.error({
                message: 'Upload failed. Doctor or Clinic id not found',
                duration: 5,
                placement: 'topRight',
            });
            return;
        }

        let newFile = new File(
            [file],
            `${docProfile?._id}-${activeClinic}-footer.${file.type.split('/')[1]}`,
        );

        if (type === 'SEND') {
            newFile = new File(
                [file],
                `${docProfile?._id}-${activeClinic}-footer-send.${file.type.split('/')[1]}`,
            );
        }

        const formData = new FormData();
        formData.append('header-footer', newFile);

        try {
            const data = await uploadRenderPdfImages(formData);

            if (data?.success) {
                saveRxConfigInState({
                    footer_img: `${data.url}?cacheoff=${randomString(4)}`,
                });
            } else {
                notification.error({
                    message: 'Failed to save Footer Image',
                    duration: 5,
                });

                saveRxConfigInState({
                    footer_img: '',
                });
            }
        } catch (error) {
            notification.error({
                message: 'Failed to save Footer Image',
                duration: 5,
            });
            console.error(error);
        }
        setIsFooterLoading(false);
    };
    const uploadAttachmentImage = async (file: File, type: 'PRINT' | 'SEND') => {
        setIsAttachmentLoading(true);
        if (!docProfile?._id && !activeClinic) {
            notification.error({
                message: 'Upload failed. Doctor or Clinic id not found',
                duration: 5,
                placement: 'topRight',
            });
            return;
        }

        let newFile = new File(
            [file],
            `${docProfile?._id}-${activeClinic}-attachment-print.${file.type.split('/')[1]}`,
        );

        if (type === 'SEND') {
            newFile = new File(
                [file],
                `${docProfile?._id}-${activeClinic}-attachment-send.${file.type.split('/')[1]}`,
            );
        }

        const formData = new FormData();
        formData.append('header-footer', newFile);

        try {
            const data = await uploadRenderPdfImages(formData);

            if (data?.success) {
                saveRxConfigInState({
                    attachment_image: `${data.url}?cacheoff=${randomString(4)}`,
                });
            } else {
                notification.error({
                    message: 'Failed to save Attachment Image',
                    duration: 5,
                });

                saveRxConfigInState({
                    attachment_image: '',
                });
            }
        } catch (error) {
            notification.error({
                message: 'Failed to save Attachment Image',
                duration: 5,
            });
            console.error(error);
        }
        setIsAttachmentLoading(false);
    };

    const uploadCroppedHeader = async (file: File) => {
        saveRxConfigInState({
            header_img: '',
        });

        uploadHeaderImage(file, templateTypeTab);
    };

    const uploadCroppedFooter = async (file: File) => {
        saveRxConfigInState({
            footer_img: '',
        });

        uploadFooterImage(file, templateTypeTab);
    };

    const uploadCroppedAttachment = async (file: File) => {
        saveRxConfigInState({
            attachment_image: '',
        });

        uploadAttachmentImage(file, templateTypeTab);
    };

    const openCropAttachment = () => {
        setCropAttachment(true);
    };

    const closeCropAttachment = () => {
        setCropAttachment(false);
    };

    if (!docProfile) {
        return <AppLoader />;
    }

    const finalRxConfig = {
        render_pdf_config: rxConfig,
        render_pdf_body_config: {
            medication_config: medElements,
            pad_elements_config: padElementsConfig,
            section_name_config: sectionNameConfig,
        },
    } as PrescriptionTemplateV2;

    const rxConf = buildConfigObject(rxConfig);
    return (
        <Fragment>
            {isConfigChange && rxConf && (
                <BlockHistoryEditTemplate docid={docProfile._id} renderConfig={rxConf} />
            )}

            {cropAttacment && (
                <ImageCrop
                    image={rxConfig?.attachment_image || ''}
                    close={closeCropAttachment}
                    isOpen={cropAttacment}
                    upload={uploadCroppedAttachment}
                />
            )}

            <div className="relative overflow-hidden flex-1 w-full flex flex-col">
                <div className="w-full flex flex-col flex-auto overflow-hidden bg-neutral-9 h-full">
                    <div className="flex w-full h-full  overflow-hidden">
                        <div
                            style={{
                                width: '33%',
                            }}
                            className="flex flex-col"
                        >
                            <NavBar
                                templateTypeTab={templateTypeTab}
                                onTemplateTypeChange={onTemplateTypeChange}
                            />

                            <div
                                className={`flex flex-col pb-16 px-16 pb-8 space-y-24 justify-between ${
                                    templateTypeTab === 'SEND' && showDigitalRxWarning
                                        ? ''
                                        : 'overflow-auto'
                                } h-full relative`}
                            >
                                {templateTypeTab === 'SEND' && showDigitalRxWarning && (
                                    <SendTemplateWarning
                                        openIsCopyToSendTemplate={openIsCopyToSendTemplate}
                                        closeShowDigitalRxWarning={closeShowDigitalRxWarning}
                                    />
                                )}
                                <div className="space-y-8 w-full">
                                    <ClinicSelectorAndCopy
                                        templateTypeTab={templateTypeTab}
                                        onClinicChange={onClinicChange}
                                        activeClinic={activeClinic}
                                        docProfile={docProfile}
                                        saveConfigToDb={saveConfigToDb}
                                        saveRxLocalConfigInState={saveRxLocalConfigInState}
                                        closeIsCopyToSendTemplate={closeIsCopyToSendTemplate}
                                        openIsCopyToSendTemplate={openIsCopyToSendTemplate}
                                        isCopyToSendTemplate={isCopyToSendTemplate}
                                    />
                                    <LetterHeadCrop
                                        uploadHeaderImage={uploadCroppedHeader}
                                        uploadFooterImage={uploadCroppedFooter}
                                    />
                                    <p className="text-center font-700 text-neutral-3">OR</p>
                                    <Space direction="vertical" className="w-full">
                                        <Header
                                            rxConfig={rxConfig}
                                            rxLocalConfig={rxLocalConfig}
                                            saveRxConfigInState={saveRxConfigInState}
                                            saveRxLocalConfigInState={saveRxLocalConfigInState}
                                            templateTypeTab={templateTypeTab}
                                            isHeaderLoading={isHeaderLoading}
                                            uploadHeaderImage={uploadHeaderImage}
                                            uploadCroppedHeader={uploadCroppedHeader}
                                        />

                                        <Footer
                                            rxConfig={rxConfig}
                                            rxLocalConfig={rxLocalConfig}
                                            saveRxConfigInState={saveRxConfigInState}
                                            saveRxLocalConfigInState={saveRxLocalConfigInState}
                                            templateTypeTab={templateTypeTab}
                                            isFooterLoading={isFooterLoading}
                                            uploadFooterImage={uploadFooterImage}
                                            openCropAttachment={openCropAttachment}
                                            uploadAttachmentImage={uploadAttachmentImage}
                                            isAttachmentLoading={isAttachmentLoading}
                                            uploadCroppedFooter={uploadCroppedFooter}
                                        />

                                        <Layout
                                            rxConfig={rxConfig}
                                            saveRxConfigInState={saveRxConfigInState}
                                        />

                                        <PatientDetails
                                            rxConfig={rxConfig}
                                            saveRxConfigInState={saveRxConfigInState}
                                        />
                                        <Vitals
                                            rxConfig={rxConfig}
                                            saveRxConfigInState={saveRxConfigInState}
                                            sectionName={
                                                finalRxConfig.render_pdf_body_config
                                                    ?.section_name_config?.['vitals']
                                            }
                                        />
                                        <GrowthChart
                                            rxConfig={rxConfig}
                                            saveRxConfigInState={saveRxConfigInState}
                                            sectionName={
                                                finalRxConfig.render_pdf_body_config
                                                    ?.section_name_config?.['growthChart']
                                            }
                                        />
                                        <Symptoms
                                            rxConfig={rxConfig}
                                            saveRxConfigInState={saveRxConfigInState}
                                            sectionName={
                                                finalRxConfig.render_pdf_body_config
                                                    ?.section_name_config?.['symptoms']
                                            }
                                        />
                                        <Diagnosis
                                            rxConfig={rxConfig}
                                            saveRxConfigInState={saveRxConfigInState}
                                            sectionName={
                                                finalRxConfig.render_pdf_body_config
                                                    ?.section_name_config?.['diagnosis']
                                            }
                                        />
                                        <MedicalHistory
                                            rxConfig={rxConfig}
                                            saveRxConfigInState={saveRxConfigInState}
                                            sectionName={
                                                finalRxConfig.render_pdf_body_config
                                                    ?.section_name_config?.['medicalHistory']
                                            }
                                        />
                                        <LabResults
                                            rxConfig={rxConfig}
                                            saveRxConfigInState={saveRxConfigInState}
                                            sectionName={
                                                finalRxConfig.render_pdf_body_config
                                                    ?.section_name_config?.['labVitals']
                                            }
                                        />

                                        <ExaminationFindings
                                            rxConfig={rxConfig}
                                            saveRxConfigInState={saveRxConfigInState}
                                            sectionName={
                                                finalRxConfig.render_pdf_body_config
                                                    ?.section_name_config?.['examinations']
                                            }
                                        />
                                        <Medications
                                            rxConfig={rxConfig}
                                            saveRxConfigInState={saveRxConfigInState}
                                            sectionName={
                                                finalRxConfig.render_pdf_body_config
                                                    ?.section_name_config?.['medications']
                                            }
                                        />
                                        <Injections
                                            rxConfig={rxConfig}
                                            saveRxConfigInState={saveRxConfigInState}
                                            sectionName={
                                                finalRxConfig.render_pdf_body_config
                                                    ?.section_name_config?.['injections']
                                            }
                                        />
                                        <LabInvestigations
                                            rxConfig={rxConfig}
                                            saveRxConfigInState={saveRxConfigInState}
                                            sectionName={
                                                finalRxConfig.render_pdf_body_config
                                                    ?.section_name_config?.['labTests']
                                            }
                                        />
                                        <DentalExaminations
                                            rxConfig={rxConfig}
                                            saveRxConfigInState={saveRxConfigInState}
                                            sectionName={
                                                finalRxConfig.render_pdf_body_config
                                                    ?.section_name_config?.['dentalExaminations']
                                            }
                                        />
                                        <DentalProcedures
                                            rxConfig={rxConfig}
                                            saveRxConfigInState={saveRxConfigInState}
                                            sectionName={
                                                finalRxConfig.render_pdf_body_config
                                                    ?.section_name_config?.['dentalProcedures']
                                            }
                                        />
                                        <OpthalAllTables
                                            rxConfig={rxConfig}
                                            saveRxConfigInState={saveRxConfigInState}
                                            sectionName={
                                                finalRxConfig.render_pdf_body_config
                                                    ?.section_name_config?.['opthalAlltable']
                                            }
                                        />
                                        <Proceduress
                                            rxConfig={rxConfig}
                                            saveRxConfigInState={saveRxConfigInState}
                                            sectionName={
                                                finalRxConfig.render_pdf_body_config
                                                    ?.section_name_config?.['procedures']
                                            }
                                        />

                                        <DyForm
                                            rxConfig={rxConfig}
                                            saveRxConfigInState={saveRxConfigInState}
                                        />
                                        <Notes
                                            rxConfig={rxConfig}
                                            saveRxConfigInState={saveRxConfigInState}
                                        />

                                        <ReferredTo
                                            rxConfig={rxConfig}
                                            saveRxConfigInState={saveRxConfigInState}
                                            sectionName={
                                                finalRxConfig.render_pdf_body_config
                                                    ?.section_name_config?.['refer']
                                            }
                                        />
                                        <Advices
                                            rxConfig={rxConfig}
                                            saveRxConfigInState={saveRxConfigInState}
                                            sectionName={
                                                finalRxConfig.render_pdf_body_config
                                                    ?.section_name_config?.['advices']
                                            }
                                        />
                                        <AdmissionAdvised
                                            rxConfig={rxConfig}
                                            saveRxConfigInState={saveRxConfigInState}
                                            sectionName={
                                                finalRxConfig.render_pdf_body_config
                                                    ?.section_name_config?.['admissonAdvised']
                                            }
                                        />
                                        <FollowUp
                                            rxConfig={rxConfig}
                                            saveRxConfigInState={saveRxConfigInState}
                                            sectionName={
                                                finalRxConfig.render_pdf_body_config
                                                    ?.section_name_config?.['followup']
                                            }
                                        />
                                        <CareCanvas
                                            rxConfig={rxConfig}
                                            saveRxConfigInState={saveRxConfigInState}
                                            sectionName={
                                                finalRxConfig.render_pdf_body_config
                                                    ?.section_name_config?.['careCanvas']
                                            }
                                        />
                                    </Space>
                                </div>
                                <div className="sticky bottom-0 z-10 flex justify-between">
                                    <button
                                        className="flex px-24 py-6 secondary-btn btn--small cursor-pointer items-center"
                                        onClick={onResetTemplate}
                                    >
                                        Reset
                                    </button>
                                    <button
                                        className="flex px-24 py-6 primary-btn btn--small cursor-pointer items-center"
                                        onClick={onSave}
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div
                            style={{
                                width: '67%',
                            }}
                            className="h-full overflow-auto bg-white"
                        >
                            <RxTemplatePdfView
                                data={{
                                    ...rxData,
                                    date: moment().toISOString(),
                                    actor: {
                                        name: getMe()?.user.fn || '-',
                                        id: getMe()?.user.oid || '-',
                                    },
                                }}
                                rxConfig={finalRxConfig}
                                rxLocalConfig={rxLocalConfig}
                                activeClinic={activeClinic}
                                saveRxConfigInState={saveRxConfigInState}
                                docProfile={docProfile}
                            />
                        </div>
                    </div>
                </div>
                <div className="w-full border-b border-neutral-7"></div>
                <style>
                    {`
                    .ant-collapse-header > div:first-child{
                        display: flex;
                        align-items: center     
                    }
                    .ant-collapse-header{
                        display: flex;
                        align-items: center     
                    }
                `}
                </style>
                {isResetTemplate && (
                    <ResetRxTemplateModal
                        onCancel={closeIsResetTemplate}
                        onReset={onDeleteTemplate}
                    />
                )}

                {isCarboneTemplatePresent && (
                    <CustomTemplatePresentModal onClickCreateNew={acknowledgeOldTemplate} />
                )}
            </div>
        </Fragment>
    );
};

export default EditTemplate;
